import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import SearchBar from '../Common/SearchBar';

const { 
  FiMenu, 
  FiBell, 
  FiSettings, 
  FiLogOut, 
  FiChevronDown,
  FiUser
} = FiIcons;

const Header = ({ onMenuClick, user, onLogout }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleSearch = (query, category) => {
    const slug = query.toLowerCase().replace(/\s+/g, '-');
    if (category === 'product' || category === 'generic-name') {
      navigate(`/product/${slug}`);
    } else if (category === 'customer') {
      navigate(`/customer/${slug}`);
    } else if (category === 'affiliation-group') {
      navigate(`/affiliation-group/${slug}`);
    } else if (category === 'ndc') {
      navigate(`/product/search?ndc=${query}`);
    } else {
      // Default search logic if 'all' is selected
      if (['clarusone', 'redoak', 'wbad'].includes(slug)) {
        navigate(`/affiliation-group/${slug}`);
      } else {
        navigate(`/customer/${slug}`);
      }
    }
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Dashboard Overview';
    if (path.startsWith('/product/')) return 'Product Analytics';
    if (path.startsWith('/customer/')) return 'Customer Intelligence';
    if (path.startsWith('/affiliation-group/')) return 'Affiliation Intelligence';
    if (path === '/iqvia') return 'IQVIA Market Data';
    return 'Analytics Dashboard';
  };

  return (
    <header className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Left Section */}
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiMenu} className="text-xl" />
          </button>
          
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{getPageTitle()}</h1>
            <p className="text-sm text-gray-600">
              Welcome back, {user?.name}
            </p>
          </div>
        </div>

        {/* Center Section - Search */}
        <div className="hidden md:block flex-1 max-w-2xl mx-8">
          <SearchBar onSearch={handleSearch} />
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-4">
          {/* Notifications */}
          <button className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
            <SafeIcon icon={FiBell} className="text-xl" />
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              3
            </span>
          </button>

          {/* Settings */}
          <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
            <SafeIcon icon={FiSettings} className="text-xl" />
          </button>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <img
                src={user?.avatar}
                alt={user?.name}
                className="w-8 h-8 rounded-full object-cover"
              />
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-600">{user?.role}</p>
              </div>
              <SafeIcon icon={FiChevronDown} className="text-gray-600" />
            </button>

            {/* User Dropdown */}
            {showUserMenu && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
              >
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-600">{user?.email}</p>
                </div>
                
                <button className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                  <SafeIcon icon={FiUser} />
                  Profile Settings
                </button>
                
                <button className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                  <SafeIcon icon={FiSettings} />
                  Preferences
                </button>
                
                <hr className="my-2" />
                
                <button
                  onClick={onLogout}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <SafeIcon icon={FiLogOut} />
                  Sign Out
                </button>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Search */}
      <div className="md:hidden mt-4">
        <SearchBar onSearch={handleSearch} />
      </div>
    </header>
  );
};

export default Header;